import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import {
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  X,
  Clock,
  ShieldOff,
  Zap,
} from 'lucide-react';
import type { Bet, BetRecommendation, DecisionStatus } from '../../types';
import { Badge } from '../common/Badge';
import { TeamLogo } from '../common/TeamLogo';
import { DataQualityBadge } from '../DataQuality/DataQualityBadge';
import { AgentPanel } from '../AgentPanel/AgentPanel';
import { formatOdds, formatMarket, formatCurrency, getTimingVerdict, formatCountdown } from '../../utils/format';
import { useAppStore } from '../../store/useAppStore';

interface BetCardProps {
  recommendation: BetRecommendation;
  existingBet?: Bet;
  compact?: boolean;
}

const QUICK_AMOUNTS = [5, 10, 20, 50];

/** Freshness thresholds in minutes */
const FRESH_THRESHOLD_MINUTES = 30;
const AGING_THRESHOLD_MINUTES = 180;
const MS_PER_MINUTE = 60_000;

/** Friendly label and color for each trust reason code */
const REASON_CODE_LABELS: Record<string, string> = {
  NO_ODDS_AVAILABLE: 'No odds available — edge cannot be computed',
  STALE_ODDS: 'Odds are stale and may not reflect current market',
  MISSING_MONEYLINE: 'Moneyline odds are missing',
  NO_SOURCES: 'No odds sources available',
  SINGLE_SOURCE: 'Single odds source — lower reliability',
  WEAK_SINGLE_SOURCE: 'Single source with low reliability score',
  HIGH_PRICE_VARIANCE: 'High price variance across sources',
  WEAK_CONSENSUS: 'Weak consensus across odds providers',
  LOW_RELIABILITY: 'Odds reliability below threshold',
  SINGLE_SOURCE_LEGACY: 'Single-source odds feed',
  GAME_STARTED: 'Game has already started — pre-game analysis only',
  INSUFFICIENT_EDGE: 'Insufficient edge to qualify as a bet',
  LOW_MATCH_CONFIDENCE: 'Low match confidence',
};

function getDecisionBadge(status: DecisionStatus | undefined) {
  const s = status ?? 'QUALIFIED_BET';
  switch (s) {
    case 'STRONG_BET':
      return { label: '🟢 STRONG BET', variant: 'win' as const };
    case 'QUALIFIED_BET':
      return { label: '🔵 QUALIFIED BET', variant: 'neutral' as const };
    case 'LEAN':
      return { label: '🟡 LEAN ONLY', variant: 'warn' as const };
    case 'NO_BET':
      return { label: '🚫 NO BET', variant: 'neutral' as const };
    case 'INVALID':
      return { label: '❌ INVALID — DATA ISSUE', variant: 'loss' as const };
    default:
      return { label: '🔵 QUALIFIED BET', variant: 'neutral' as const };
  }
}

function getOddsFreshnessLabel(freshnessMs: number | undefined) {
  if (freshnessMs === undefined) return null;
  const minutes = freshnessMs / MS_PER_MINUTE;
  if (minutes < FRESH_THRESHOLD_MINUTES)
    return { label: `🟢 Fresh (${Math.round(minutes)}m ago)`, cls: 'text-win', window: null };
  if (minutes < AGING_THRESHOLD_MINUTES)
    return {
      label: `⚠️ Aging (${Math.round(minutes)}m ago)`,
      cls: 'text-warn',
      window: '· Window narrowing',
    };
  return {
    label: `❌ Stale (${Math.round(minutes / 60)}h ago)`,
    cls: 'text-loss',
    window: '· Verify odds before placing',
  };
}

function getActionGuidance(status: DecisionStatus): { text: string; cls: string } {
  switch (status) {
    case 'STRONG_BET':
      return { text: 'Recommended Action: Signal released — strong validated edge', cls: 'text-win' };
    case 'QUALIFIED_BET':
      return {
        text: 'Recommended Action: Operate within your standard unit allocation',
        cls: 'text-brand-light',
      };
    case 'LEAN':
      return { text: 'For reference only — no release recommended', cls: 'text-warn' };
    case 'NO_BET':
      return { text: 'Skip this game — insufficient edge', cls: 'text-gray-500' };
    case 'INVALID':
      return { text: 'Cannot verify data — recommendation blocked', cls: 'text-loss' };
    default:
      return { text: 'Review analysis before acting', cls: 'text-gray-500' };
  }
}

function getRiskTier(units: number): { label: string; cls: string } {
  if (units <= 1) return { label: 'Low', cls: 'text-win' };
  if (units <= 2) return { label: 'Medium', cls: 'text-warn' };
  return { label: 'High', cls: 'text-loss' };
}

export function BetCard({ recommendation: rec, existingBet, compact }: BetCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [stakeInput, setStakeInput] = useState('');
  const [oddsInput, setOddsInput] = useState('');
  const [isMultiplier, setIsMultiplier] = useState(false);
  const [multiplierBonusInput, setMultiplierBonusInput] = useState('0.00');
  const [lineInput, setLineInput] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isRunLine = rec.market === 'runline' || rec.market === 'first5_runline';
  const runLineMatch = rec.selection.match(/^(.+?)\s+([+-]\d+(?:\.\d+)?)$/);
  const selectionTeam = runLineMatch?.[1] ?? rec.selection;
  const defaultLine = runLineMatch?.[2] ?? '-1.5';
  const { placeBet, bankrollSettings, exposure, games } = useAppStore();

  // Look up full game time (ISO) so we can compute time-until-first-pitch
  const gameTimeIso = games.find((g) => g.game.gamePk === rec.gamePk)?.game.gameTime;

  // Normalize legacy 'BET' status from cached recommendations to 'QUALIFIED_BET'
  const rawStatus = rec.decisionStatus ?? 'QUALIFIED_BET';
  const decisionStatus: DecisionStatus =
    (rawStatus as string) === 'BET' ? 'QUALIFIED_BET' : (rawStatus as DecisionStatus);
  const isActionable = decisionStatus === 'STRONG_BET' || decisionStatus === 'QUALIFIED_BET';
  const showEdge = decisionStatus !== 'INVALID';
  const decisionBadge = getDecisionBadge(decisionStatus);
  const freshness = getOddsFreshnessLabel(rec.oddsFreshnessMs);
  const actionGuidance = getActionGuidance(decisionStatus);

  const recommended = rec.recommendedStake.recommendedAmount;

  // Focus input when modal opens
  useEffect(() => {
    if (showStakeModal) {
      setStakeInput(recommended.toFixed(2));
      setOddsInput(rec.odds.toString());
      setIsMultiplier(false);
      setMultiplierBonusInput('0.00');
      setLineInput(defaultLine);
      setTimeout(() => inputRef.current?.select(), 50);
    }
  }, [showStakeModal]);

  const effectiveOdds = parseInt(oddsInput) || rec.odds;

  const effectiveSelection =
    isRunLine && lineInput
      ? `${selectionTeam} ${lineInput.startsWith('+') || lineInput.startsWith('-') ? lineInput : `-${lineInput}`}`
      : rec.selection;

  const handlePlace = async () => {
    const amount = parseFloat(stakeInput);
    if (!amount || amount <= 0) return;
    setPlacing(true);
    setShowConfirmModal(false);
    try {
      const units = bankrollSettings
        ? amount / bankrollSettings.unitSize
        : rec.recommendedStake.recommendedUnits;
      const multiplierBonus = isMultiplier ? parseFloat(multiplierBonusInput) || 0 : 0;
      await placeBet({
        eventPk: rec.eventPk ?? String(rec.gamePk),
        gamePk: rec.gamePk,
        gameDate: rec.gameDate,
        awayTeam: rec.awayTeam,
        homeTeam: rec.homeTeam,
        market: rec.market,
        selection: effectiveSelection,
        odds: effectiveOdds,
        stakeAmount: amount,
        stakeUnits: units,
        modelProbability: rec.modelProbability,
        edge: rec.edge,
        confidence: rec.confidence,
        recommendationId: rec.id,
        isMultiplier,
        multiplierBonus,
      });
    } finally {
      setPlacing(false);
    }
  };

  const edgePct = (rec.edge * 100).toFixed(1);
  const confidencePct = Math.round(rec.confidence * 100);
  const modelPct = Math.round(rec.modelProbability * 100);
  const impliedPct = Math.round(rec.impliedProbability * 100);

  // Detect steam move from MarketAgent reasoning (dataPoint format: "Steam: Xpts/Ymin")
  const steamDataPoint = rec.agentOpinions?.market?.reasoning?.find((r) =>
    r.dataPoint?.startsWith('Steam:'),
  );
  const steamSummary = steamDataPoint?.text ?? null;
  const countdown = formatCountdown(gameTimeIso);
  const timingVerdict = isActionable
    ? getTimingVerdict(rec.oddsFreshnessMs, gameTimeIso, !!steamSummary)
    : null;

  const confidenceVariant =
    rec.confidenceLevel === 'high' ? 'win' : rec.confidenceLevel === 'medium' ? 'warn' : 'neutral';

  // Bankroll context for enhanced stake display
  const bankrollPct = bankrollSettings
    ? ((rec.recommendedStake.recommendedAmount / bankrollSettings.currentBankroll) * 100).toFixed(1)
    : null;
  const riskTier = getRiskTier(rec.recommendedStake.recommendedUnits);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        'card overflow-hidden transition-all duration-200',
        decisionStatus === 'STRONG_BET' && rec.confidenceLevel === 'high' && 'border-win/20',
        decisionStatus === 'STRONG_BET' && rec.edge > 0.07 && 'border-glow-win',
        decisionStatus === 'QUALIFIED_BET' && 'border-brand/20',
        decisionStatus === 'INVALID' && 'border-loss/20 opacity-80',
        decisionStatus === 'LEAN' && 'border-warn/20',
      )}
    >
      {/* Decision Status Banner */}
      <div
        className={clsx(
          'px-4 py-1.5 flex items-center justify-between text-xs font-semibold border-b',
          decisionStatus === 'STRONG_BET' && 'bg-win/10 border-win/20 text-win',
          decisionStatus === 'QUALIFIED_BET' && 'bg-brand/10 border-brand/20 text-brand-light',
          decisionStatus === 'LEAN' && 'bg-warn/10 border-warn/20 text-warn',
          decisionStatus === 'NO_BET' && 'bg-surface-3 border-surface-4 text-gray-400',
          decisionStatus === 'INVALID' && 'bg-loss/10 border-loss/20 text-loss',
        )}
      >
        <span>{decisionBadge.label}</span>
        <div className="flex items-center gap-2">
          {countdown && (
            <span className={clsx('text-[10px] font-semibold flex items-center gap-0.5', countdown.cls)}>
              <Clock className="w-3 h-3" />
              {countdown.text}
            </span>
          )}
          {freshness && (
            <span className={clsx('text-[10px] font-normal flex items-center gap-1', freshness.cls)}>
              {freshness.label}
              {freshness.window && (
                <span className={clsx('font-semibold', freshness.cls)}>{freshness.window}</span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Action Guidance Strip */}
      <div
        className={clsx(
          'px-4 py-1 text-[11px] border-b border-surface-4',
          decisionStatus === 'STRONG_BET' && 'bg-win/5',
          decisionStatus === 'QUALIFIED_BET' && 'bg-brand/5',
          decisionStatus === 'LEAN' && 'bg-warn/5',
          decisionStatus === 'INVALID' && 'bg-loss/5',
          decisionStatus === 'NO_BET' && 'bg-surface-1',
        )}
      >
        <span className={actionGuidance.cls}>{actionGuidance.text}</span>
      </div>

      {/* Header */}
      <div className="px-4 py-3 bg-surface-2 border-b border-surface-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-mono uppercase">
                {formatMarket(rec.market)}
              </span>
              <Badge variant={confidenceVariant as 'win' | 'warn' | 'neutral'} size="xs">
                {rec.confidenceLevel}
              </Badge>
              {showEdge && rec.edge > 0.07 && (
                <Badge variant="win" size="xs">
                  HIGH EDGE
                </Badge>
              )}
              {rec.dataReliabilityScore < 0.6 && (
                <Badge variant="warn" size="xs">
                  DATA RISK
                </Badge>
              )}
              {steamSummary && (
                <span
                  title={`Sharp action detected — line movement accelerating. ${steamSummary}`}
                  className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-warn/10 text-warn border border-warn/20 cursor-help"
                >
                  <Zap className="w-2.5 h-2.5" />
                  STEAM
                </span>
              )}
            </div>
            <div className="mt-1 text-base font-semibold text-gray-100">{rec.selection}</div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
              <TeamLogo name={rec.awayTeam} size="xs" />
              <span>{rec.awayTeam.split(' ').pop()}</span>
              <span>@</span>
              <TeamLogo name={rec.homeTeam} size="xs" />
              <span>{rec.homeTeam.split(' ').pop()}</span>
            </div>
          </div>

          <div className="flex-shrink-0 text-right">
            <div
              className={clsx(
                'text-xl font-bold font-mono',
                rec.odds === 0 ? 'text-gray-600' : rec.odds > 0 ? 'text-win' : 'text-gray-200',
              )}
            >
              {rec.odds === 0 ? 'N/A' : formatOdds(rec.odds)}
            </div>
            {showEdge ? (
              <div
                className={clsx(
                  'text-sm font-semibold',
                  rec.edge >= 0.05 ? 'text-win' : rec.edge >= 0.03 ? 'text-warn' : 'text-gray-400',
                )}
              >
                {edgePct}% edge
              </div>
            ) : (
              <div className="text-xs text-gray-600 italic">edge hidden</div>
            )}
          </div>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-4 divide-x divide-surface-4 border-b border-surface-4">
        <MetricCell label="Implied" value={`${impliedPct}%`} />
        <MetricCell label="Model" value={`${modelPct}%`} accent={showEdge} muted={!showEdge} />
        <MetricCell label="Confidence" value={`${confidencePct}%`} />
        <MetricCell
          label="Data"
          value={`${Math.round(rec.dataReliabilityScore * 100)}%`}
          valueClass={
            rec.dataReliabilityScore >= 0.75
              ? 'text-win'
              : rec.dataReliabilityScore >= 0.5
                ? 'text-warn'
                : 'text-loss'
          }
        />
      </div>

      {/* For / Against — always visible */}
      {(rec.reasonsFor.length > 0 || rec.reasonsAgainst.length > 0) && (
        <div className="grid grid-cols-2 gap-4 px-4 py-3 border-b border-surface-4 bg-surface-1">
          <div>
            <div className="text-xs font-medium text-win mb-1.5 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> For
            </div>
            <div className="space-y-1">
              {rec.reasonsFor.slice(0, 3).map((r, i) => (
                <div key={i} className="text-xs text-gray-400 flex items-start gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-win flex-shrink-0 mt-1.5" />
                  <span className="leading-snug">{r.text}</span>
                </div>
              ))}
              {rec.reasonsFor.length === 0 && (
                <span className="text-xs text-gray-600">No strong positive signals</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-loss mb-1.5 flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> Against
            </div>
            <div className="space-y-1">
              {rec.reasonsAgainst.slice(0, 3).map((r, i) => (
                <div key={i} className="text-xs text-gray-400 flex items-start gap-1.5">
                  <div className="w-1 h-1 rounded-full bg-loss flex-shrink-0 mt-1.5" />
                  <span className="leading-snug">{r.text}</span>
                </div>
              ))}
              {rec.reasonsAgainst.length === 0 && (
                <span className="text-xs text-gray-600">No significant concerns</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Trust Reason Codes — shown for non-actionable states */}
      {!isActionable && rec.trustReasonCodes && rec.trustReasonCodes.length > 0 && (
        <div
          className={clsx(
            'px-4 py-2 border-b border-surface-4',
            decisionStatus === 'INVALID' ? 'bg-loss/5' : 'bg-surface-1',
          )}
        >
          <div className="flex items-center gap-1 mb-1 text-xs font-medium text-gray-400">
            <ShieldOff className="w-3 h-3" />
            {decisionStatus === 'INVALID' ? 'Trust / Data Failures' : 'Caution Signals'}
          </div>
          {decisionStatus === 'INVALID' && (
            <p className="text-xs text-loss/80 mb-1.5 font-medium">
              We cannot proceed — one or more critical data requirements failed verification.
            </p>
          )}
          <div className="space-y-0.5">
            {rec.trustReasonCodes.map((code) => (
              <div key={code} className="text-xs text-gray-500 flex items-start gap-1.5">
                <span className={decisionStatus === 'INVALID' ? 'text-loss' : 'text-warn'}>•</span>
                <span className="font-mono text-[10px] text-gray-600 shrink-0">{code}</span>
                <span className="text-gray-500">{REASON_CODE_LABELS[code] ?? code}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expand button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between text-xs text-gray-500 hover:bg-surface-2 transition-colors"
      >
        <span>Reasoning & Evidence</span>
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-surface-4 bg-surface-1">
          {/* Reasons For / Against */}
          <div className="grid grid-cols-2 gap-4 pt-3">
            <div>
              <div className="text-xs font-medium text-win mb-2 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> FOR
              </div>
              <div className="space-y-2">
                {rec.reasonsFor.length > 0 ? (
                  rec.reasonsFor.map((r, i) => (
                    <div key={i} className="text-xs text-gray-400 flex items-start gap-1.5">
                      <div className="w-1 h-1 rounded-full bg-win flex-shrink-0 mt-1.5" />
                      <span>{r.text}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-600">No strong positive signals</p>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-loss mb-2 flex items-center gap-1">
                <TrendingDown className="w-3 h-3" /> AGAINST
              </div>
              <div className="space-y-2">
                {rec.reasonsAgainst.length > 0 ? (
                  rec.reasonsAgainst.map((r, i) => (
                    <div key={i} className="text-xs text-gray-400 flex items-start gap-1.5">
                      <div className="w-1 h-1 rounded-full bg-loss flex-shrink-0 mt-1.5" />
                      <span>{r.text}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-gray-600">No significant concerns</p>
                )}
              </div>
            </div>
          </div>

          {/* Key Risks */}
          {rec.keyRisks.length > 0 && (
            <div className="pt-2 border-t border-surface-4">
              <div className="text-xs font-medium text-warn mb-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> KEY RISK DRIVERS
              </div>
              <div className="space-y-1">
                {rec.keyRisks.map((risk, i) => (
                  <div key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                    <span className="text-warn">⚠</span> {risk}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stake recommendation — only for BET */}
          {isActionable && (
            <div className="pt-2 border-t border-surface-4 bg-surface-2 -mx-4 px-4 py-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-gray-400">Recommended Stake</div>
                <div className="text-xs text-gray-600">{rec.recommendedStake.reasoning}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-200">
                  {rec.recommendedStake.recommendedUnits.toFixed(1)} units ·{' '}
                  <span className="text-brand">
                    {formatCurrency(rec.recommendedStake.recommendedAmount)}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  Max: {formatCurrency(rec.recommendedStake.maxAmount)}
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                {bankrollPct && (
                  <span>
                    Exposure: <span className="text-gray-300">{bankrollPct}% of bankroll</span>
                  </span>
                )}
                <span>
                  Risk: <span className={riskTier.cls}>{riskTier.label}</span>
                </span>
                <span>
                  Max downside:{' '}
                  <span className="text-gray-300">
                    {formatCurrency(rec.recommendedStake.recommendedAmount)}
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Data Quality */}
          <div className="pt-1 border-t border-surface-4">
            <DataQualityBadge quality={rec.dataQuality} showDetails />
          </div>

          {/* Agent Panel Toggle */}
          <div className="border-t border-surface-4 pt-2">
            <button
              onClick={() => setShowAgents(!showAgents)}
              className="text-xs text-brand hover:text-brand-light flex items-center gap-1"
            >
              {showAgents ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              {showAgents ? 'Hide' : 'Show'} Agent Breakdown
            </button>
            {showAgents && (
              <div className="mt-3">
                <AgentPanel recommendation={rec} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timing Verdict — synthesized bet-now-vs-wait signal for actionable bets */}
      {timingVerdict && (
        <div className={clsx('px-4 py-2 border-t border-surface-4 text-[11px] font-medium flex items-center gap-1.5', timingVerdict.bgCls, timingVerdict.cls)}>
          <Clock className="w-3 h-3 flex-shrink-0" />
          {timingVerdict.text}
        </div>
      )}

      {/* Action Footer */}
      <div className="px-4 py-3 border-t border-surface-4 bg-surface-2 flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          Consensus:{' '}
          <span className="font-mono text-gray-300">{(rec.consensusScore * 100).toFixed(0)}%</span>
          {' · '}
          <span className="text-gray-600">
            {new Date(rec.generatedAt).toLocaleTimeString('en-US', {
              timeZone: 'America/Los_Angeles',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })}
          </span>
        </div>

        {isActionable ? (
          !existingBet ? (
            <button
              onClick={() => setShowStakeModal(true)}
              disabled={placing}
              className={clsx(
                'btn-primary text-xs py-1.5 px-3',
                placing && 'opacity-50 cursor-not-allowed',
              )}
            >
              {placing ? 'Placing...' : 'Place Bet'}
            </button>
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-win">
              <CheckCircle className="w-3.5 h-3.5" />
              Placed
            </div>
          )
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 italic">
            <XCircle className="w-3.5 h-3.5" />
            Not actionable
          </div>
        )}
      </div>

      {/* Stake Modal — only shown for actionable bets */}
      {showStakeModal && isActionable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowStakeModal(false)}
          />

          {/* Panel */}
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="relative bg-surface-1 border border-surface-4 rounded-lg shadow-xl w-72 p-5"
          >
            {/* Close */}
            <button
              onClick={() => setShowStakeModal(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="mb-1 text-sm font-semibold text-gray-200">Place Bet</div>
            <div className="text-xs text-gray-500 mb-4">
              {rec.selection} · {formatMarket(rec.market)}
            </div>

            {/* Run Line input — only for runline markets */}
            {isRunLine && (
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Run Line (Spread)</label>
                <input
                  type="text"
                  value={lineInput}
                  onChange={(e) => setLineInput(e.target.value)}
                  className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-3 py-2 text-sm text-gray-200 outline-none font-mono"
                  placeholder="-1.5"
                />
                {lineInput !== defaultLine && (
                  <div className="text-[10px] text-warn mt-1">
                    Advisor: {defaultLine} · Using: {lineInput || '—'}
                  </div>
                )}
              </div>
            )}

            {/* Odds input */}
            <div className="mb-3">
              <label className="block text-xs text-gray-500 mb-1">Odds (American)</label>
              <input
                type="number"
                step="1"
                value={oddsInput}
                onChange={(e) => setOddsInput(e.target.value)}
                className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-3 py-2 text-sm text-gray-200 outline-none font-mono"
              />
              {oddsInput !== rec.odds.toString() && parseInt(oddsInput) !== rec.odds && (
                <div className="text-[10px] text-warn mt-1">
                  Advisor: {formatOdds(rec.odds)} · Using:{' '}
                  {oddsInput && parseInt(oddsInput) ? formatOdds(parseInt(oddsInput)) : '—'}
                </div>
              )}
            </div>

            {/* Quick-pick buttons */}
            <div className="flex gap-2 mb-3">
              {QUICK_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setStakeInput(amt.toFixed(2))}
                  className={clsx(
                    'flex-1 py-1.5 text-xs rounded border transition-colors',
                    stakeInput === amt.toFixed(2)
                      ? 'bg-brand border-brand text-white'
                      : 'border-surface-4 text-gray-400 hover:border-brand hover:text-gray-200',
                  )}
                >
                  ${amt}
                </button>
              ))}
            </div>

            {/* Stake input */}
            <div className="relative mb-4">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                $
              </span>
              <input
                ref={inputRef}
                type="number"
                min="0.01"
                step="0.01"
                value={stakeInput}
                onChange={(e) => setStakeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && parseFloat(stakeInput) > 0) {
                    setShowStakeModal(false);
                    setShowConfirmModal(true);
                  }
                }}
                placeholder={recommended.toFixed(2)}
                className="w-full bg-surface-3 border border-surface-4 focus:border-brand rounded px-3 py-2 pl-7 text-sm text-gray-200 outline-none"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-600">
                Rec: {formatCurrency(recommended)}
              </span>
            </div>

            {/* STN Multiplier toggle */}
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => setIsMultiplier((v) => !v)}
                  className={clsx(
                    'w-9 h-5 rounded-full flex items-center transition-colors',
                    isMultiplier ? 'bg-brand' : 'bg-surface-4',
                  )}
                >
                  <div
                    className={clsx(
                      'w-4 h-4 rounded-full bg-white shadow transition-transform mx-0.5',
                      isMultiplier ? 'translate-x-4' : 'translate-x-0',
                    )}
                  />
                </div>
                <span className="text-xs text-gray-400">STN Multiplier</span>
                {isMultiplier && (
                  <span className="text-[10px] bg-brand/20 text-brand px-1.5 py-0.5 rounded font-semibold tracking-wide uppercase">
                    Active
                  </span>
                )}
              </label>
              {isMultiplier && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Bonus ($)</span>
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                      +$
                    </span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={multiplierBonusInput}
                      onChange={(e) => setMultiplierBonusInput(e.target.value)}
                      className="w-full bg-surface-3 border border-brand/40 focus:border-brand rounded px-2 py-1.5 pl-6 text-sm text-brand outline-none font-mono"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Potential return preview */}
            {parseFloat(stakeInput) > 0 && effectiveOdds !== 0 && (
              <div className="text-xs text-gray-500 mb-4">
                {(() => {
                  const stake = parseFloat(stakeInput);
                  const baseWin =
                    effectiveOdds > 0
                      ? stake * (effectiveOdds / 100)
                      : stake * (100 / Math.abs(effectiveOdds));
                  const bonus = isMultiplier ? parseFloat(multiplierBonusInput) || 0 : 0;
                  const totalReturn = stake + baseWin + bonus;
                  return (
                    <>
                      To win:{' '}
                      <span className="text-win font-mono">{formatCurrency(baseWin + bonus)}</span>
                      {bonus > 0 && (
                        <span className="text-brand font-mono">
                          {' '}
                          (+{formatCurrency(bonus)} bonus)
                        </span>
                      )}
                      {' · '}Total return:{' '}
                      <span className="text-gray-300 font-mono">{formatCurrency(totalReturn)}</span>
                    </>
                  );
                })()}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => setShowStakeModal(false)}
                className="flex-1 btn-ghost text-xs py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowStakeModal(false);
                  setShowConfirmModal(true);
                }}
                disabled={!parseFloat(stakeInput) || parseFloat(stakeInput) <= 0}
                className="flex-1 btn-primary text-xs py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Review →
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirmation modal — read-only review before bet commits */}
      {showConfirmModal &&
        isActionable &&
        (() => {
          const stake = parseFloat(stakeInput);
          const baseWin =
            effectiveOdds > 0
              ? stake * (effectiveOdds / 100)
              : stake * (100 / Math.abs(effectiveOdds));
          const bonus = isMultiplier ? parseFloat(multiplierBonusInput) || 0 : 0;
          const totalReturn = stake + baseWin + bonus;
          const bankrollAfter = (bankrollSettings?.currentBankroll ?? 0) - stake;
          const exposureAfter = (exposure?.totalOpen ?? 0) + stake;
          const exposurePct = bankrollSettings
            ? ((exposureAfter / bankrollSettings.currentBankroll) * 100).toFixed(1)
            : null;
          const bankrollLow = bankrollSettings
            ? bankrollAfter < bankrollSettings.currentBankroll * 0.2
            : false;
          const oddsChanged = effectiveOdds !== rec.odds;

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-black/60"
                onClick={() => setShowConfirmModal(false)}
              />
              <motion.div
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.15 }}
                className="relative bg-surface-1 border border-surface-4 rounded-lg shadow-xl w-80 p-5"
              >
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="absolute top-3 right-3 text-gray-600 hover:text-gray-400"
                >
                  <X className="w-4 h-4" />
                </button>

                <div className="mb-1 text-sm font-semibold text-gray-200">Confirm Bet</div>
                <div className="text-xs text-gray-500 mb-4">Review before placing</div>

                {/* Bet summary */}
                <div className="bg-surface-2 rounded-md p-3 mb-3 space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Selection</span>
                    <span className="text-gray-200 font-medium text-right max-w-[55%] truncate">
                      {effectiveSelection}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Market</span>
                    <span className="text-gray-300">{formatMarket(rec.market)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Odds</span>
                    <span
                      className={clsx('font-mono', oddsChanged ? 'text-warn' : 'text-gray-300')}
                    >
                      {formatOdds(effectiveOdds)}
                      {oddsChanged && (
                        <span className="text-gray-600 ml-1">(rec: {formatOdds(rec.odds)})</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Stake</span>
                    <span className="text-gray-200 font-mono">{formatCurrency(stake)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">To win</span>
                    <span className="text-win font-mono">
                      {formatCurrency(baseWin + bonus)}
                      {bonus > 0 && (
                        <span className="text-brand ml-1">(+{formatCurrency(bonus)} bonus)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-surface-4 pt-1.5 mt-1">
                    <span className="text-gray-400 font-medium">Total return</span>
                    <span className="text-gray-200 font-mono font-semibold">
                      {formatCurrency(totalReturn)}
                    </span>
                  </div>
                </div>

                {/* Bankroll impact */}
                {bankrollSettings && (
                  <div className="bg-surface-2 rounded-md p-3 mb-3 space-y-1.5 text-xs">
                    <div className="text-gray-500 font-medium mb-1">Bankroll Impact</div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current bankroll</span>
                      <span className="text-gray-300 font-mono">
                        {formatCurrency(bankrollSettings.currentBankroll)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">After bet</span>
                      <span
                        className={clsx('font-mono', bankrollLow ? 'text-loss' : 'text-gray-300')}
                      >
                        {formatCurrency(bankrollAfter)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current exposure</span>
                      <span className="text-gray-300 font-mono">
                        {formatCurrency(exposure?.totalOpen ?? 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">New exposure</span>
                      <span className="text-gray-300 font-mono">
                        {formatCurrency(exposureAfter)}
                        {exposurePct && (
                          <span className="text-gray-600 ml-1">({exposurePct}%)</span>
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {/* Violation warnings */}
                {exposure?.violations && exposure.violations.length > 0 && (
                  <div className="mb-3 space-y-1">
                    {exposure.violations.map((v, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-1.5 text-[11px] text-warn bg-warn/10 rounded px-2 py-1.5"
                      >
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>{v}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowConfirmModal(false);
                      setShowStakeModal(true);
                    }}
                    className="flex-1 btn-ghost text-xs py-2"
                  >
                    Edit Stake
                  </button>
                  <button
                    onClick={handlePlace}
                    disabled={placing}
                    className="flex-1 btn-primary text-xs py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {placing ? 'Placing…' : 'Place Bet'}
                  </button>
                </div>
              </motion.div>
            </div>
          );
        })()}
    </motion.div>
  );
}

function MetricCell({
  label,
  value,
  accent,
  muted,
  valueClass,
}: {
  label: string;
  value: string;
  accent?: boolean;
  muted?: boolean;
  valueClass?: string;
}) {
  return (
    <div className="py-2 px-3 flex flex-col items-center gap-0.5">
      <span className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</span>
      <span
        className={clsx(
          'text-sm font-semibold font-mono',
          valueClass ?? (muted ? 'text-gray-600' : accent ? 'text-brand' : 'text-gray-200'),
        )}
      >
        {value}
      </span>
    </div>
  );
}
